import { DateTime, type DateTimeUnit } from "@dmca/utils/luxon";

const DurationUnitType = ["y", "M", "w", "d", "h", "m", "s"] as const;
type DurationUnitType = (typeof DurationUnitType)[number];

/**
 * Adapted from [Grafana](https://github.com/grafana/grafana/blob/main/packages/grafana-data/src/datetime/datemath.ts)
 *
 * Parses a string format similar to Grafana's (now - 6d) into a DateTime object. If the string is not a valid date, it will return undefined.
 * @returns DateTime object
 */
export function parseRelativeTime(
  value: string | DateTime | undefined | null,
  dateFormat: string,
  reference: DateTime = DateTime.now()
): DateTime<true> | undefined {
  if (!value) return undefined;
  if (DateTime.isDateTime(value))
    return value.isValid ? (value as DateTime<true>) : undefined;
  value = value.trim();
  let time: DateTime;
  let mathString = "";
  let index: number;
  let parseString: string;

  if (value.substring(0, 3) === "now") {
    time = reference;
    mathString = value.substring("now".length);
  } else {
    index = value.indexOf("||");
    if (index === -1) {
      parseString = value;
      mathString = "";
    } else {
      parseString = value.substring(0, index);
      mathString = value.substring(index + 2);
    }
    time = DateTime.fromFormat(parseString, dateFormat);
  }

  if (!mathString.length) {
    return time.isValid ? (time as DateTime<true>) : undefined;
  }

  const result = parseDateMath(mathString, time);
  return result?.isValid ? (result as DateTime<true>) : undefined;
}

/**
 * Parses math part of the time string and shifts supplied time according to that math. See unit tests for examples.
 * @param mathString
 * @param time
 * @param roundUp If true it will round the time to endOf time unit, otherwise to startOf time unit.
 */
function parseDateMath(
  mathString: string,
  time: DateTime
): DateTime | undefined {
  const math = mathString.replace(/\s/g, "");
  const unitsMap = {
    y: "year",
    M: "month",
    w: "week",
    d: "day",
    h: "hour",
    m: "minute",
    s: "second",
  } satisfies Record<DurationUnitType, DateTimeUnit>;
  let result = time;
  let offset = 0;
  while (offset < math.length) {
    const match = /^([/+-])(\d+(?:\.\d+)?)?([yMwdhms])/.exec(
      math.slice(offset)
    );
    if (!match) return undefined;
    const [, operator, amount, unit] = match;
    const number = amount === undefined ? 1 : Number(amount);
    if (!Number.isFinite(number) || (operator === "/" && number !== 1))
      return undefined;
    const parsedUnit = unitsMap[unit as DurationUnitType];
    result =
      operator === "/"
        ? result.startOf(parsedUnit)
        : operator === "+"
        ? result.plus({ [parsedUnit]: number })
        : result.minus({ [parsedUnit]: number });
    if (!result.isValid) return undefined;
    offset += match[0].length;
  }
  return result;
}

// Numeric bounds are elapsed seconds: hours/minutes/seconds round-trip across
// DST transitions, while calendar-day/week math can have a different duration.
const units = {
  h: 3600,
  m: 60,
  s: 1,
} satisfies Record<string, number>;

export const secondsToRelativeFormat = (seconds: number): string => {
  if (!Number.isFinite(seconds)) return "";
  if (seconds === 0) return "now";
  const amount = Math.abs(seconds);
  const prefix = seconds > 0 ? "now-" : "now+";
  for (const [unit, size] of Object.entries(units)) {
    if (amount >= size && amount % size === 0)
      return `${prefix}${amount / size}${unit}`;
  }
  return `${prefix}${amount.toLocaleString("en-US", {
    useGrouping: false,
    maximumFractionDigits: 20,
  })}s`;
};

/** Relative seconds are offsets into the past; negatives are future offsets. */
export function timeframeInstant(
  value: number | DateTime,
  reference: DateTime
): number | undefined {
  const instant =
    typeof value === "number"
      ? Number.isFinite(value)
        ? reference.toMillis() - value * 1000
        : NaN
      : value.toMillis();
  return Number.isFinite(instant) ? instant : undefined;
}

export function validTimeframe(
  start: number | DateTime,
  end: number | DateTime,
  reference: DateTime = DateTime.now()
) {
  const startInstant = timeframeInstant(start, reference);
  const endInstant = timeframeInstant(end, reference);
  return (
    startInstant !== undefined &&
    endInstant !== undefined &&
    startInstant < endInstant
  );
}
